import type { HumanDecisionRejectReason, HumanDecisionSignal } from "../../queries/useAgentLearning";

export type SignalGroup = {
  type: string;
  label: string;
  items: HumanDecisionSignal[];
};

export const rejectReasonLabels: Record<HumanDecisionRejectReason, string> = {
  checker_noise: "Checker noise",
  operator_bookkeeping: "Operator bookkeeping",
  non_decision: "Non-decision",
};

export const rejectReasonDescriptions: Record<HumanDecisionRejectReason, string> = {
  checker_noise: "False alarm from the checker. The case was already handled correctly. Nothing to learn.",
  operator_bookkeeping: "An admin / record-keeping action (logging a call, updating a field), not a customer-handling decision.",
  non_decision: "No real decision was made. A status echo or capture artifact, nothing teachable.",
};

const decisionTypeLabels: Record<string, string> = {
  disposition: "Dispositions",
  reminder: "Reminders",
  manual_reply: "Manual replies",
  review_verdict: "Review verdicts",
  label_correction: "Label corrections",
  sms_brain_correction: "SMS corrections",
  vonigo_transition: "Vonigo transitions",
  vonigo_manual_transition: "Vonigo transitions",
};

export function signalTypeLabel(type: string) {
  return decisionTypeLabels[type] || type.replace(/_/g, " ");
}

export function groupSignalsByType(items: HumanDecisionSignal[]): SignalGroup[] {
  const map = new Map<string, HumanDecisionSignal[]>();
  for (const item of items) {
    const key = item.decision_type || "unknown";
    map.set(key, [...(map.get(key) || []), item]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => signalTypeLabel(a).localeCompare(signalTypeLabel(b)))
    .map(([type, groupItems]) => ({
      type,
      label: signalTypeLabel(type),
      items: [...groupItems].sort((a, b) => b.captured_at.localeCompare(a.captured_at) || b.id - a.id),
    }));
}

export function formatSignalDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function compactSignalText(value: string | null | undefined, fallback = "Not captured") {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 180 ? `${text.slice(0, 177)}...` : text;
}

export function buildCorrectionPayload(value: string) {
  return {
    correction: {
      human_decision: compactSignalText(value, ""),
    },
  };
}

export function buildRejectPayload(reason: HumanDecisionRejectReason) {
  return { reason };
}
